import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import CustomSelect from '../ui/CustomSelect'; // Assuming a custom select component exists
import { startLiveRun } from '../../api/botsApi';
import { fetchAccounts } from '../../api/api';
import { APP_ACTION_SURFACES } from '../../access/surfaces';
import { useActionAccess } from '../../access/actionAccess';

const LaunchLiveRunModal = ({ botVersionId, onSuccess, onClose }) => {
  const startLiveRunAccess = useActionAccess(APP_ACTION_SURFACES.botVersionStartLiveRun);
  const [instrumentSymbol, setInstrumentSymbol] = useState('');
  const [accountId, setAccountId] = useState('');
  const [timeframe, setTimeframe] = useState('M1');
  const [decisionMode, setDecisionMode] = useState('CANDLE');
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const timeframeOptions = [
    { value: 'M1', label: 'M1' },
    { value: 'M5', label: 'M5' },
    { value: 'M15', label: 'M15' },
    { value: 'M30', label: 'M30' },
    { value: 'H1', label: 'H1' },
    { value: 'H4', label: 'H4' },
    { value: 'D1', label: 'D1' },
  ];

  const decisionModeOptions = [
    { value: 'CANDLE', label: 'CANDLE' },
    { value: 'TICK', label: 'TICK' },
  ];

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const userAccounts = await fetchAccounts();
        setAccounts(userAccounts.map(acc => ({ value: acc.id, label: `${acc.name} (${acc.platform})` })));
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setError("Failed to load accounts.");
      }
    };
    loadAccounts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startLiveRunAccess.ensureAccess()) {
      return;
    }
    setIsLoading(true);
    setError(null);

    if (!instrumentSymbol || !accountId) {
      setError("Instrument Symbol and Account are required.");
      setIsLoading(false);
      return;
    }

    const launchData = {
      bot_version_id: botVersionId,
      instrument_symbol: instrumentSymbol,
      account_id: accountId,
      timeframe,
      decision_mode: decisionMode,
    };

    try {
      const result = await startLiveRun(launchData);
      if (onSuccess) {
        onSuccess(result);
      }
      onClose();
    } catch (err) {
      console.error("Failed to launch live run:", err);
      setError(err.message || "Failed to launch live run.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-backgroundSurface p-6 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-xl font-semibold text-textPrimary mb-4">Start Live Run</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="text-destructive">{error}</p>}
          {startLiveRunAccess.disabled && <p className="text-sm text-textSecondary">{startLiveRunAccess.blockedMessage}</p>}
          <div>
            <p className="text-sm text-textSecondary">Bot Version ID: {botVersionId ? `${botVersionId.substring(0,8)}...` : '-'}</p>
          </div>
          <div>
            <label htmlFor="instrumentSymbol" className="block text-sm font-medium text-textPrimary">
              Instrument Symbol
            </label>
            <Input
              id="instrumentSymbol"
              type="text"
              value={instrumentSymbol}
              onChange={(e) => setInstrumentSymbol(e.target.value)}
              placeholder="e.g., EURUSD"
              required
              className="mt-1"
            />
          </div>
          <div>
            <label htmlFor="timeframe" className="block text-sm font-medium text-textPrimary">
              Timeframe
            </label>
            <CustomSelect
              id="timeframe"
              options={timeframeOptions}
              value={timeframe}
              onChange={setTimeframe}
              placeholder="Select timeframe"
              className="mt-1"
            />
          </div>
          <div>
            <label htmlFor="decisionMode" className="block text-sm font-medium text-textPrimary">
              Decision Mode
            </label>
            <CustomSelect
              id="decisionMode"
              options={decisionModeOptions}
              value={decisionMode}
              onChange={setDecisionMode}
              placeholder="Select decision mode"
              className="mt-1"
            />
          </div>
          <div>
            <label htmlFor="accountId" className="block text-sm font-medium text-textPrimary">
              Account
            </label>
            <CustomSelect
              id="accountId"
              options={accounts}
              value={accountId}
              onChange={setAccountId}
              placeholder="Select an account"
              required
              className="mt-1"
            />
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || startLiveRunAccess.disabled}
              title={startLiveRunAccess.disabled ? startLiveRunAccess.blockedMessage : undefined}
            >
              {isLoading ? 'Starting...' : 'Start Live Run'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LaunchLiveRunModal;
